import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Container,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  CheckCircle,
  Business,
  Person,
  CorporateFare,
  Star,
} from "@mui/icons-material";
import {
  useGetPackagesDetailsQuery,
  usePostAgencyPkgDetailsMutation,
} from "../../../redux/api/packagesApi";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import BackButton from "../../../components/BackButton/BackButton";

const purchaseSource = [
  { id: 1, label: "Bank" },
  { id: 2, label: "Cash" },
];

const PackageSelection = () => {
  const [purchaseSourceValue, setPurchaseSourceValue] = useState("");
  const currentAgency = useSelector(selectCurrentagency);

  const agencyId = currentAgency?.agencyid;
   const countryId = currentAgency?.countryid
  const [selectedPackage, setSelectedPackage] = useState("");

  const { data: packages, isLoading, error } = useGetPackagesDetailsQuery(countryId);
  const [postAgencyPkgDetails, { isLoading: isSelecting }] =
    usePostAgencyPkgDetailsMutation();

  const packagesData =
    packages?.map((pkg) => ({
      id: pkg.id,
      name: pkg.name,
      price: `${pkg.countryid==16?'₨':'AED'}${pkg.amount.toLocaleString()}`,
      period: "/year",
      description:
        pkg.description
          ?.split("\n")
          .map((line) => line.trim())
          .filter(Boolean) || [], // Split into array of points
      color: pkg.id === 1 ? "#2196f3" : pkg.id === 2 ? "#4caf50" : "#ff9800",
      icon:
        pkg.id === 1 ? (
          <Person sx={{ fontSize: 40 }} />
        ) : pkg.id === 2 ? (
          <Business sx={{ fontSize: 40 }} />
        ) : (
          <CorporateFare sx={{ fontSize: 40 }} />
        ),
    })) || [];

  const handlePackageChange = (event) => {
    setSelectedPackage(event.target.value);
  };

  const handleSelectPackage = async () => {
    if (!selectedPackage) return toast.error("Select a package");
    if (!purchaseSourceValue) return toast.error("Select purchase source");

    try {
      await postAgencyPkgDetails({
        agencyid: agencyId,
        purchaseSource: purchaseSourceValue === "Bank" ? 1 : 2,
        pkgid: selectedPackage,
        purchaseDate: new Date().toISOString(),
      }).unwrap();
    } catch (error) {
      console.error("Failed to select package:", error);
    
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load packages. Please try again later.
      </Alert>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
        <BackButton />
      {/* Header */}
      <Box textAlign="center" mb={5}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          Choose Your Perfect Plan
        </Typography>
        <Typography variant="h6" color="text.secondary" maxWidth="600px" mx="auto">
          Select the package that best fits your agency's needs and start building amazing projects
        </Typography>
      </Box>

      {/* Purchase Source */}
    <Box
  sx={{
    maxWidth: "400px",
    pl: { xs: 0, sm: 2 },
    mb: 4,
  }}
>
  <FormControl fullWidth>
    <InputLabel id="purchase-source-label">Purchase Source</InputLabel>
    <Select
      labelId="purchase-source-label"
      id="purchase-source"
      value={purchaseSourceValue}
      label="Purchase Source"      
      onChange={(e) => setPurchaseSourceValue(e.target.value)}
    >
      {purchaseSource.map((option) => (
        <MenuItem key={option.id} value={option.label}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Box>


      {/* Package Cards */}
      <RadioGroup value={selectedPackage} onChange={handlePackageChange}>
        <Grid container sx={{p:'0 !important', width:'100%'}} spacing={4} justifyContent="center">
          {packagesData.map((pkg) => (
            <Grid sx={{width:'100%'}} item xs={12} md={4} key={pkg.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                   width:'100%',
                  flexDirection: "column",
                  position: "relative",
                  cursor: "pointer",
                  overflow: "visible",
                  transition: "all 0.3s ease",
                  border: selectedPackage === pkg.id ? 3 : 1,
                  borderColor: selectedPackage === pkg.id ? pkg.color : "divider",
                  transform: selectedPackage === pkg.id ? "translateY(-8px)" : "none",
                  boxShadow: selectedPackage === pkg.id ? 8 : 2,
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
                onClick={() => setSelectedPackage(pkg.id)}
              >
                {pkg.popular && (
                  <Chip
                    label="Most Popular"
                    color="primary"
                    size="small"
                    icon={<Star />}
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 1,
                      fontWeight: "bold",
                    }}
                  />
                )}

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <FormControlLabel
                    value={pkg.id}
                    control={<Radio sx={{ display: "none" }} />}
                    label=""
                    sx={{ m: 0, display: "none" }}
                  />

                  <Box textAlign="center" mb={3}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 2,
                        borderRadius: "50%",
                        bgcolor: `${pkg.color}20`,
                        color: pkg.color,
                        mb: 2,
                      }}
                    >
                      {pkg.icon}
                    </Box>

                    <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                      {pkg.name}
                    </Typography>

                    <Box display="flex" alignItems="baseline" justifyContent="center">
                      <Typography variant="h3" component="span" fontWeight="bold" color={pkg.color}>
                        {pkg.price}
                      </Typography>
                      <Typography variant="h6" component="span" color="text.secondary">
                        {pkg.period}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Description Points with Check Icon */}
                <List dense>
  {pkg.description.map((htmlPoint, index) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlPoint;
    const text = tempDiv.textContent || tempDiv.innerText || "";

    return (
      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
      <ListItemIcon sx={{ minWidth: 36 }}>
  <CheckCircle sx={{ color: "#4caf50" }} fontSize="small" />
</ListItemIcon>
        <ListItemText
          primary={text}
          primaryTypographyProps={{ variant: "body2" }}
        />
      </ListItem>
    );
  })}
</List>

                </CardContent>

                {selectedPackage === pkg.id && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      bgcolor: pkg.color,
                      borderRadius: "50%",
                      p: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckCircle sx={{ color: "white", fontSize: 24 }} />
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </RadioGroup>

      {/* Continue Button */}
      <Box textAlign="center" mt={5}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSelectPackage}
          disabled={!selectedPackage | isSelecting}
          sx={{
            px: 6,
            py: 1.5,
            fontSize: "1.1rem",
            borderRadius: 2,
            textTransform: "none",
            fontWeight: "bold",
            color:'white'
          }}
        >
          {isSelecting ? "Selecting Package..." : "Continue with Selected Package"}
        </Button>
      </Box>

      {/* Help Section */}
      <Paper sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="h6" gutterBottom textAlign="center">
          Need Help Choosing?
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          All plans include a 14-day free trial. You can upgrade or downgrade at
          any time. Contact our support team if you need assistance choosing the
          right package for your agency.
        </Typography>
      </Paper>
    </Container>
  );
};

export default PackageSelection;
